import type { PublicDisplayAgendaItem } from "@/modules/room-displays/domain/contracts/room-displays";
import { cn } from "@devhop/ui";
import { useEffect, useRef, useState } from "react";
import { DisplayClock } from "./DisplayClock";

const UNASSIGNED_ROOM = "ບໍ່ລະບຸຫ້ອງ";
/** minutes before start -> yellow highlight */
const SOON_WINDOW_MINUTES = 30;

function typeLabel(type: PublicDisplayAgendaItem["type"]): string {
  if (type === "class") return "ຄລາດສ໌";
  if (type === "private") return "ຮຽນສ່ວນຕົວ";
  return "ຈອງຫ້ອງ";
}

function parseHm(t: string): number {
  const [h, m] = t.slice(0, 5).split(":").map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

function formatHm(t: string): string {
  return t.slice(0, 5);
}

function useNowMinutes(intervalMs = 15_000): number {
  const [now, setNow] = useState(() => {
    const d = new Date();
    return d.getHours() * 60 + d.getMinutes();
  });

  useEffect(() => {
    const tick = () => {
      const d = new Date();
      setNow(d.getHours() * 60 + d.getMinutes());
    };
    tick();
    const id = window.setInterval(tick, intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);

  return now;
}

const LAO_WEEKDAYS = [
  "ວັນອາທິດ",
  "ວັນຈັນ",
  "ວັນອັງຄານ",
  "ວັນພຸດ",
  "ວັນພະຫັດ",
  "ວັນສຸກ",
  "ວັນເສົາ",
] as const;

const LAO_MONTHS = [
  "ມັງກອນ",
  "ກຸມພາ",
  "ມີນາ",
  "ເມສາ",
  "ພຶດສະພາ",
  "ມິຖຸນາ",
  "ກໍລະກົດ",
  "ສິງຫາ",
  "ກັນຍາ",
  "ຕຸລາ",
  "ພະຈິກ",
  "ທັນວາ",
] as const;

function formatDateLabel(date: string): string {
  const d = new Date(`${date}T12:00:00`);
  if (Number.isNaN(d.getTime())) return date;
  const weekday = LAO_WEEKDAYS[d.getDay()] ?? "";
  const month = LAO_MONTHS[d.getMonth()] ?? "";
  return `${weekday} ${d.getDate()} ${month} ${d.getFullYear()}`;
}

function groupByRoom(items: PublicDisplayAgendaItem[]) {
  const map = new Map<string, PublicDisplayAgendaItem[]>();

  for (const item of items) {
    const room = item.roomName?.trim() || UNASSIGNED_ROOM;
    const list = map.get(room);
    if (list) list.push(item);
    else map.set(room, [item]);
  }

  const rooms = [...map.keys()].sort((a, b) => {
    if (a === UNASSIGNED_ROOM) return 1;
    if (b === UNASSIGNED_ROOM) return -1;
    return a.localeCompare(b, "lo");
  });

  return rooms.map((room) => ({
    room,
    items: (map.get(room) ?? []).sort((a, b) =>
      a.startTime.localeCompare(b.startTime),
    ),
  }));
}

function sessionState(start: number, end: number, now: number) {
  const isLive = start <= now && now < end;
  const isPast = end <= now;
  const isSoon =
    !isLive && !isPast && start > now && start - now <= SOON_WINDOW_MINUTES;
  return { isLive, isPast, isSoon };
}

export function DisplayAgendaPanel({
  date,
  items,
}: {
  date: string;
  items: PublicDisplayAgendaItem[];
}) {
  const now = useNowMinutes();
  const focusRef = useRef<HTMLLIElement | null>(null);
  const groups = groupByRoom(items);
  const total = items.length;

  let focusKey: string | null = null;
  let focusKind: "live" | "soon" | null = null;
  for (const group of groups) {
    for (const item of group.items) {
      const start = parseHm(item.startTime);
      const end = parseHm(item.endTime);
      const { isLive, isSoon } = sessionState(start, end, now);
      if (isLive) {
        focusKey = `${item.type}-${item.id}`;
        focusKind = "live";
        break;
      }
      if (!focusKey && isSoon) {
        focusKey = `${item.type}-${item.id}`;
        focusKind = "soon";
      }
    }
    if (focusKind === "live") break;
  }

  useEffect(() => {
    if (!focusRef.current) return;
    focusRef.current.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [focusKey]);

  return (
    <div className="flex h-full min-h-0 flex-col bg-white text-neutral-900">
      <header className="flex shrink-0 items-center justify-between gap-3 border-neutral-200 border-b px-3 py-3 md:px-5 md:py-4">
        <div className="min-w-0">
          <p className="truncate font-bold text-neutral-900 text-lg md:text-2xl">
            ຕາຕະລາງມື້ນີ້
          </p>
          <p className="truncate font-semibold text-neutral-600 text-sm md:text-lg">
            {formatDateLabel(date)}
            {total > 0 ? (
              <span>
                {" "}
                · {total} ລາຍການ
              </span>
            ) : null}
          </p>
        </div>
        <DisplayClock />
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-2.5 py-2.5 md:px-4 md:py-3">
        {total === 0 ? (
          <div className="flex h-full min-h-40 items-center justify-center px-3 text-center">
            <p className="font-semibold text-neutral-500 text-xl md:text-2xl">
              ບໍ່ມີຕາຕະລາງມື້ນີ້
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-4 md:gap-5">
            {groups.map((group) => {
              const hasLive = group.items.some((item) => {
                const { isLive } = sessionState(
                  parseHm(item.startTime),
                  parseHm(item.endTime),
                  now,
                );
                return isLive;
              });
              const hasSoon = group.items.some((item) => {
                const { isSoon } = sessionState(
                  parseHm(item.startTime),
                  parseHm(item.endTime),
                  now,
                );
                return isSoon;
              });

              return (
                <section key={group.room}>
                  <div
                    className={cn(
                      "sticky top-0 z-10 mb-1.5 flex items-center justify-between gap-2 border-b bg-white/95 px-1 py-1.5 backdrop-blur-sm",
                      hasLive
                        ? "border-red-300"
                        : hasSoon
                          ? "border-amber-300"
                          : "border-neutral-200",
                    )}
                  >
                    <h2
                      className={cn(
                        "truncate font-bold text-base tracking-tight md:text-xl",
                        hasLive
                          ? "text-red-700"
                          : hasSoon
                            ? "text-amber-700"
                            : "text-neutral-900",
                      )}
                    >
                      {group.room}
                    </h2>
                    <span className="shrink-0 font-semibold text-neutral-500 text-sm tabular-nums md:text-base">
                      {group.items.length}
                    </span>
                  </div>

                  <ul className="flex flex-col">
                    {group.items.map((item) => {
                      const start = parseHm(item.startTime);
                      const end = parseHm(item.endTime);
                      const { isLive, isPast, isSoon } = sessionState(
                        start,
                        end,
                        now,
                      );
                      const instructors = (item.instructorNames ?? [])
                        .filter(Boolean)
                        .join(", ");
                      const key = `${item.type}-${item.id}`;

                      return (
                        <li
                          key={key}
                          ref={key === focusKey ? focusRef : undefined}
                          className={cn(
                            "grid grid-cols-[6.5rem_1fr] items-start gap-x-2.5 border-neutral-100 border-b px-1 py-2 last:border-b-0 md:grid-cols-[7.5rem_1fr] md:gap-x-3 md:py-2.5",
                            isLive && "rounded-sm bg-red-50",
                            isSoon && !isLive && "rounded-sm bg-amber-50",
                            isPast && !isLive && "opacity-40",
                          )}
                        >
                          <div
                            className={cn(
                              "pt-0.5 text-right tabular-nums leading-tight",
                              isLive
                                ? "text-red-700"
                                : isSoon
                                  ? "text-amber-800"
                                  : "text-neutral-800",
                            )}
                          >
                            <div className="font-bold text-sm md:text-lg">
                              {formatHm(item.startTime)}–{formatHm(item.endTime)}
                            </div>
                            {isLive ? (
                              <div className="mt-0.5 font-bold text-xs tracking-wide md:text-sm">
                                ຕອນນີ້
                              </div>
                            ) : null}
                            {isSoon && !isLive ? (
                              <div className="mt-0.5 font-bold text-xs tracking-wide md:text-sm">
                                ໃກ້ເລີ່ມ
                              </div>
                            ) : null}
                          </div>

                          <div
                            className={cn(
                              "min-w-0 border-l-2 pl-2.5 leading-snug md:pl-3",
                              isLive
                                ? "border-red-600"
                                : isSoon
                                  ? "border-amber-500"
                                  : "border-neutral-200",
                            )}
                          >
                            <div className="flex min-w-0 items-baseline gap-2">
                              <span
                                className={cn(
                                  "shrink-0 font-bold text-xs md:text-sm",
                                  isLive
                                    ? "text-red-700"
                                    : isSoon
                                      ? "text-amber-700"
                                      : "text-neutral-600",
                                )}
                              >
                                {typeLabel(item.type)}
                              </span>
                              <p
                                className={cn(
                                  "min-w-0 truncate font-bold text-base md:text-xl",
                                  isLive || isSoon
                                    ? "text-neutral-950"
                                    : "text-neutral-900",
                                )}
                              >
                                {item.title || "—"}
                              </p>
                            </div>
                            {instructors ? (
                              <p className="mt-0.5 truncate font-semibold text-neutral-600 text-sm md:text-base">
                                {instructors}
                              </p>
                            ) : null}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
